
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ExternalLink, Download, FileText, Camera, Palette } from 'lucide-react';

const PressResourcesCard = () => {
  return (
    <section className="relative">
      {/* Background decoration */}
      <div className="absolute inset-0 bg-white rounded-3xl shadow-2xl transform rotate-1"></div>
      <div className="relative bg-white rounded-3xl shadow-xl p-8 md:p-12 border border-[#894D1D]/10">
        
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-[#894D1D] to-[#698235] rounded-full mb-6">
            <Download className="h-8 w-8 text-white" />
          </div>
          <h2 className="text-4xl font-montserrat font-bold text-[#894D1D] mb-4">
            Press Resources & Assets
          </h2>
          <p className="text-xl text-[#894D1D]/70 font-montserrat max-w-3xl mx-auto">
            Download tour photos, logos, and official press releases for media use.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 mb-12">
          {/* Assets Column */}
          <div className="space-y-6">
            <div className="flex items-center gap-3 mb-4">
              <Palette className="h-6 w-6 text-[#698235]" />
              <h3 className="text-2xl font-montserrat font-semibold text-[#894D1D]">
                Visual Assets
              </h3>
            </div>
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <Camera className="h-5 w-5 text-[#698235] mt-1 flex-shrink-0" />
                <span className="text-[#894D1D]/90 font-montserrat">High-resolution tour photos</span>
              </div>
              <div className="flex items-start gap-3">
                <Palette className="h-5 w-5 text-[#698235] mt-1 flex-shrink-0" />
                <span className="text-[#894D1D]/90 font-montserrat">Official logos (PNG format)</span>
              </div>
            </div>
          </div>
          
          {/* Press Releases Column */}
          <div className="space-y-6">
            <div className="flex items-center gap-3 mb-4">
              <FileText className="h-6 w-6 text-[#698235]" />
              <h3 className="text-2xl font-montserrat font-semibold text-[#894D1D]">
                Press Releases
              </h3>
            </div>
            <div className="space-y-4">
              {[
                "Legendary Cat-Historian Launches New Cat-Themed Walking Tour in NYC",
                "Cats About Town Tours Launches Financial District Cat Tour with Lower East Side Tour Coming in Spring 2025",
                "NYC's Premier Walking Tour for Cat Lovers Sells Out Rapidly; Exciting Spring 2025 Tours Announced",
                "Cats About Town Tours Launches Petition to Honor Brooklyn's Hero Cat Jerry Fox"
              ].map((release, index) => (
                <div key={index} className="flex items-start gap-3">
                  <FileText className="h-5 w-5 text-[#698235] mt-1 flex-shrink-0" />
                  <span className="text-[#894D1D]/90 font-montserrat text-sm leading-relaxed">{release}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Quick Facts Card */}
        <div className="bg-gradient-to-r from-[#F7F0DE] to-[#FAF2E8] rounded-2xl p-8 mb-12">
          <h3 className="text-2xl font-montserrat font-semibold text-[#894D1D] mb-6 text-center">
            Quick Facts
          </h3>
          <div className="grid md:grid-cols-2 gap-6">
            {[
              { label: "Founded", value: "August 2023" },
              { label: "Founders", value: "Peggy Gavan (historian), Dan Rimada (producer)" },
              { label: "Tours", value: "Brooklyn Heights, Financial District & Lower East Side" },
              { label: "Duration", value: "2-hour walking tours" },
              { label: "Press Features", value: "NBC, The Guardian, People, amNY" },
              { label: "Contact", value: "catsabouttowntours@gmail.com" }
            ].map((fact, index) => (
              <div key={index} className="flex flex-col">
                <div className="text-sm font-medium text-[#894D1D]/70 font-montserrat uppercase tracking-wide">
                  {fact.label}
                </div>
                <div className="text-[#894D1D] font-montserrat font-medium">
                  {fact.value}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* CTA Button */}
        <div className="text-center">
          <a
            href="https://drive.google.com/drive/folders/your-press-kit-folder"
            target="_blank"
            rel="noopener noreferrer"
          >
            <Button size="xl" className="font-montserrat text-lg px-12 py-4 rounded-full bg-gradient-to-r from-[#894D1D] to-[#698235] hover:from-[#783C0C] hover:to-[#5E6C2F] shadow-lg hover:shadow-xl transform hover:-translate-y-1 transition-all duration-300">
              <Download className="mr-3 h-6 w-6" />
              Access Press Kit
              <ExternalLink className="ml-3 h-5 w-5" />
            </Button>
          </a>
          <p className="text-sm text-[#894D1D]/60 font-montserrat mt-4">
            Opens Google Drive in new window
          </p>
        </div>
      </div>
    </section>
  );
};

export default PressResourcesCard;
